import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ContactPage.css";

const WHATSAPP_NUMBER = "358443519265";
const PRINT_ORDER_DRAFT_KEY = "paranoidi_print_order_draft";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_ALLOWED_PATTERN = /^[+()\d\s-]+$/;

const CONTACT_TOPICS = [
  { value: "myynti", key: "sales" },
  { value: "tuki", key: "support" },
  { value: "tilaus", key: "billing" },
  { value: "yhteistyo", key: "partnership" },
  { value: "muu", key: "other" },
];

const FIELD_FOCUS_ORDER = [
  "name",
  "email",
  "phone",
  "company",
  "businessId",
  "streetAddress",
  "postalCode",
  "city",
  "topic",
  "message",
  "acceptedProof",
];

const DELIVERY_METHOD_KEYS = {
  pickup: "pickup",
  post_small: "postSmall",
  post_express: "postExpress",
  courier: "courier",
};

function isValidPhone(value) {
  const phone = String(value || "").trim();
  const digitCount = phone.replace(/\D/g, "").length;

  return (
    PHONE_ALLOWED_PATTERN.test(phone) &&
    digitCount >= 6 &&
    digitCount <= 15
  );
}

function isValidFinnishBusinessId(value) {
  const match = String(value || "")
    .trim()
    .match(/^(\d{7})-(\d)$/);

  if (!match) {
    return false;
  }

  const weights = [7, 9, 10, 5, 8, 4, 2];
  const sum = match[1]
    .split("")
    .reduce((total, digit, index) => {
      return total + Number(digit) * weights[index];
    }, 0);

  const remainder = sum % 11;

  if (remainder === 1) {
    return false;
  }

  const expectedCheckDigit = remainder === 0 ? 0 : 11 - remainder;
  return Number(match[2]) === expectedCheckDigit;
}

function getApiBaseUrl() {
  return (
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api")
  );
}

function readStoredJson(storage, key) {
  try {
    const rawValue = storage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
}

function getPrintOrderDraft() {
  return readStoredJson(window.sessionStorage, PRINT_ORDER_DRAFT_KEY);
}

function formatEuro(value, locale, t) {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return t("contactPage.common.confirmSeparately", {
      defaultValue: "Vahvistetaan erikseen",
    });
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function formatDate(value, locale) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getOrderProductName(draft, t) {
  return draft?.productType === "coaster"
    ? t("contactPage.order.products.coaster", { defaultValue: "Tuopinaluset" })
    : t("contactPage.order.products.stand", { defaultValue: "Kangasständi" });
}

function getOrderQuantityText(draft, t) {
  if (draft?.productType === "coaster") {
    const amount = Number(draft?.coasterAmount) || 0;
    return amount
      ? t("contactPage.order.quantityPieces", {
          count: amount,
          defaultValue: "{{count}} kpl",
        })
      : t("contactPage.order.quantityMissing", {
          defaultValue: "Määrä puuttuu",
        });
  }

  const quantity = Math.max(1, Number(draft?.standQuantity) || 1);
  return t("contactPage.order.quantityPieces", {
    count: quantity,
    defaultValue: "{{count}} kpl",
  });
}

function getOrderDeliveryLabel(draft, t) {
  const method = String(draft?.deliveryMethod || "pickup").trim();
  const key = DELIVERY_METHOD_KEYS[method];

  if (key) {
    return t(`contactPage.order.deliveryMethods.${key}`, {
      defaultValue: draft?.deliveryLabel || (method === "pickup" ? "Nouto" : method),
    });
  }

  return draft?.deliveryLabel || t("contactPage.order.deliveryMethods.pickup", {
    defaultValue: "Nouto",
  });
}

function getOrderDeliveryText(draft, t, locale) {
  const label = getOrderDeliveryLabel(draft, t);
  const price = Number(draft?.deliveryPrice) || 0;

  return `${label} – ${formatEuro(price, locale, t)}`;
}

function formatParanoidiUrl(value, t) {
  const url = String(value || "").trim();

  if (!url) {
    return t("contactPage.order.addressMissing", {
      defaultValue: "Osoite puuttuu",
    });
  }

  return url.replace(/^https?:\/\//i, "");
}

function localizeServerFieldErrors(fieldErrors, t, isEnglish) {
  if (!fieldErrors || typeof fieldErrors !== "object" || Array.isArray(fieldErrors)) {
    return {};
  }

  if (!isEnglish) {
    return fieldErrors;
  }

  const result = {};

  Object.keys(fieldErrors).forEach((fieldName) => {
    result[fieldName] = t(`contactPage.validation.serverFields.${fieldName}`, {
      defaultValue: t("contactPage.validation.invalidField", {
        defaultValue: "Check this field.",
      }),
    });
  });

  return result;
}

function FieldLabel({ children, required = false }) {
  return (
    <span className="mb-2 block text-xs font-black uppercase tracking-[0.14em] text-zinc-300">
      {children}
      {required ? <span className="ml-1 text-red-400">*</span> : null}
    </span>
  );
}

function FieldError({ id, children }) {
  if (!children) {
    return null;
  }

  return (
    <span
      id={id}
      className="mt-2 block text-sm font-bold leading-6 text-red-300"
    >
      {children}
    </span>
  );
}

function TextInput({ className = "", hasError = false, ...props }) {
  return (
    <input
      {...props}
      className={[
        "contact-field",
        "w-full rounded-2xl border bg-black/45 px-4 py-3.5",
        "text-sm font-semibold text-white outline-none transition",
        "placeholder:text-zinc-600",
        "focus:border-red-400/60 focus:bg-black/65 focus:ring-4 focus:ring-red-500/10",
        hasError
          ? "border-red-400/70 bg-red-500/[0.06]"
          : "border-white/10",
        className,
      ].join(" ")}
    />
  );
}

function TextArea({ className = "", hasError = false, ...props }) {
  return (
    <textarea
      {...props}
      className={[
        "contact-field",
        "min-h-36 w-full resize-y rounded-2xl border bg-black/45 px-4 py-3.5",
        "text-sm font-semibold leading-7 text-white outline-none transition",
        "placeholder:text-zinc-600",
        "focus:border-red-400/60 focus:bg-black/65 focus:ring-4 focus:ring-red-500/10",
        hasError
          ? "border-red-400/70 bg-red-500/[0.06]"
          : "border-white/10",
        className,
      ].join(" ")}
    />
  );
}

function OrderSummary({ draft, t, locale }) {
  // Vanha luonnos voi puuttua coasterPrintMode-kentästä.
  // Jos kääntöpuolen vedos löytyy, tulkitaan tuote kaksipuoliseksi.
  const isDoubleCoaster =
    draft?.productType === "coaster" &&
    (draft?.coasterPrintMode === "double" ||
      Boolean(draft?.coasterBackPreviewDataUrl));

  const coasterPrintLabel = isDoubleCoaster
    ? t("contactPage.order.print.double", { defaultValue: "Kaksipuolinen" })
    : t("contactPage.order.print.single", { defaultValue: "Yksipuolinen" });

  const previewImages = [
    {
      label: t("contactPage.order.previews.stand", {
        defaultValue: "Ständin esikatselu",
      }),
      value: draft?.standPreviewDataUrl,
    },
    {
      label: isDoubleCoaster
        ? t("contactPage.order.previews.coasterFront", {
            defaultValue: "Tuopinalusen etupuoli",
          })
        : t("contactPage.order.previews.coasterProof", {
            defaultValue: "Tuopinalusen vedos",
          }),
      value: draft?.coasterFrontPreviewDataUrl,
    },
    {
      label: t("contactPage.order.previews.coasterBack", {
        defaultValue: "Tuopinalusen kääntöpuoli",
      }),
      value: isDoubleCoaster ? draft?.coasterBackPreviewDataUrl : "",
    },
  ].filter((item) => item.value);

  // ProductCustomizer tallentaa productPriceen tuotteen hinnan
  // ja estimatedPriceen tuotteen + valitun toimituksen loppusumman.
  const productPrice = Number(draft?.productPrice) || 0;
  const deliveryPrice = Number(draft?.deliveryPrice) || 0;

  const totalPrice = Number.isFinite(Number(draft?.estimatedPrice))
    ? Number(draft.estimatedPrice)
    : productPrice + deliveryPrice;

  return (
    <section className="contact-order-summary rounded-[2rem] border border-red-400/25 bg-gradient-to-br from-red-500/12 via-zinc-950 to-black p-5 shadow-2xl shadow-black/40 sm:p-7">
      <div className="flex flex-col gap-5 border-b border-white/10 pb-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-red-300">
            {t("contactPage.order.eyebrow", { defaultValue: "Tilauksesi" })}
          </p>

          <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">
            {getOrderProductName(draft, t)}
          </h2>

          <p className="mt-2 text-sm font-bold text-zinc-400">
            {draft?.performerName ||
              t("contactPage.order.performerMissing", {
                defaultValue: "Esiintyjän nimi puuttuu",
              })}
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/40 px-5 py-4 sm:text-right">
          <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            {t("contactPage.order.total", { defaultValue: "Yhteensä" })}
          </p>

          <p className="mt-1 text-2xl font-black text-white">
            {formatEuro(totalPrice, locale, t)}
          </p>

          <p className="mt-2 text-xs font-bold text-zinc-500">
            {t("contactPage.order.priceBreakdown", {
              product: formatEuro(productPrice, locale, t),
              delivery: formatEuro(deliveryPrice, locale, t),
              defaultValue: "Tuote {{product}} · Toimitus {{delivery}}",
            })}
          </p>
        </div>
      </div>

      <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-black/25 p-4 sm:col-span-2">
          <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            {t("contactPage.order.paranoidiAddress", {
              defaultValue: "Paranoidi-osoite",
            })}
          </dt>

          <dd className="mt-2 break-words text-sm font-bold text-zinc-200">
            {formatParanoidiUrl(draft?.qrValue, t)}
          </dd>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
          <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            {t("contactPage.order.delivery", { defaultValue: "Toimitus" })}
          </dt>
          <dd className="mt-2 font-black text-white">
            {getOrderDeliveryText(draft, t, locale)}
          </dd>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
          <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            {t("contactPage.order.quantity", { defaultValue: "Määrä" })}
          </dt>

          <dd className="mt-2 font-black text-white">
            {getOrderQuantityText(draft, t)}
          </dd>
        </div>

        {draft?.productType === "coaster" && (
          <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
            <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
              {t("contactPage.order.printing", { defaultValue: "Painatus" })}
            </dt>

            <dd className="mt-2 font-black text-white">
              {coasterPrintLabel}
            </dd>
          </div>
        )}

        {draft?.productType !== "coaster" && (
          <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
            <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
              {t("contactPage.order.size", { defaultValue: "Koko" })}
            </dt>
            <dd className="mt-2 font-black text-white">
              {draft?.standSize || "600 × 2300 mm"}
            </dd>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
          <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            {t("contactPage.order.ownLogo", { defaultValue: "Oma logo" })}
          </dt>
          <dd className="mt-2 font-black text-white">
            {draft?.logoName ||
              t("contactPage.order.noOwnLogo", {
                defaultValue: "Ei omaa logoa",
              })}
          </dd>
        </div>

        {draft?.createdAt && (
          <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
            <dt className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
              {t("contactPage.order.draftSaved", {
                defaultValue: "Luonnos tallennettu",
              })}
            </dt>
            <dd className="mt-2 font-black text-white">
              {formatDate(draft.createdAt, locale)}
            </dd>
          </div>
        )}
      </dl>

      {previewImages.length > 0 && (
        <div
          className={`mt-5 grid gap-4 ${
            isDoubleCoaster ? "sm:grid-cols-2" : "grid-cols-1"
          }`}
        >
          {previewImages.map((preview) => (
            <figure
              key={preview.label}
              className="overflow-hidden rounded-2xl border border-red-400/20 bg-black/35 p-4"
            >
              <img
                src={preview.value}
                alt={preview.label}
                className={`mx-auto w-full rounded-xl object-contain ${
                  draft?.productType === "stand"
                    ? "max-h-[44rem]"
                    : "max-h-[28rem]"
                }`}
              />

              <figcaption className="mt-4 text-center text-xs font-black uppercase tracking-[0.12em] text-zinc-300">
                {preview.label}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {draft?.logoDataUrl && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-black/25 p-4">
          <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            {t("contactPage.order.attachedLogo", {
              defaultValue: "Tilaukseen liitettävä logo",
            })}
          </p>

          <div className="mt-4 flex min-h-20 items-center justify-center rounded-2xl border border-dashed border-white/10 bg-white p-3">
            <img
              src={draft.logoDataUrl}
              alt={t("contactPage.order.attachedLogoAlt", {
                defaultValue: "Tilaukseen liitettävä esiintyjän logo",
              })}
              className="max-h-20 max-w-[70%] object-contain"
            />
          </div>
        </div>
      )}

      <a
        href="/taydenna-tilaustasi#editori"
        className="mt-6 inline-flex rounded-full border border-white/15 px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-white no-underline transition hover:border-white/30 hover:bg-white/10"
      >
        {t("contactPage.order.backToEditor", {
          defaultValue: "Palaa muokkaamaan tuotetta",
        })}
      </a>
    </section>
  );
}

export default function ContactPage() {
  const { t, i18n } = useTranslation("admin");
  const isEnglish = i18n.resolvedLanguage?.toLowerCase().startsWith("en");
  const emailLanguage = isEnglish ? "en" : "fi";
  const locale = isEnglish ? "en-GB" : "fi-FI";

  const formRef = useRef(null);
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  const isPrintOrderMode = searchParams.get("aihe") === "painotuote";
  const [orderDraft] = useState(() =>
    isPrintOrderMode ? getPrintOrderDraft() : null
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    businessId: "",
    topic: "myynti",
    message: "",
    streetAddress: "",
    postalCode: "",
    city: "",
    acceptedProof: false,
  });

  const [status, setStatus] = useState({
    type: "idle",
    message: "",
    orderNumber: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const whatsappMessage = encodeURIComponent(
    isPrintOrderMode
      ? t("contactPage.whatsapp.printOrderMessage", {
          defaultValue: "Moi! Haluaisin kysyä Paranoidin painotuotetilauksesta.",
        })
      : t("contactPage.whatsapp.contactMessage", {
          defaultValue: "Moi! Haluaisin kysyä Paranoidista.",
        })
  );

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

  // Pikkupaketti, Express ja lähetti tarvitsevat toimitusosoitteen.
  // Nouto on ainoa vaihtoehto, jossa osoitetta ei kysytä.
  const isShipping =
    isPrintOrderMode && orderDraft?.deliveryMethod !== "pickup";

  useEffect(() => {
    if (!isPrintOrderMode || !orderDraft) {
      return;
    }

    setForm((current) => ({
      ...current,
      topic: "painotuote",
    }));
  }, [isPrintOrderMode, orderDraft]);

  function updateField(event) {
    const { name, value, type, checked } = event.target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));

    setFieldErrors((current) => {
      if (!current[name]) {
        return current;
      }

      const next = { ...current };
      delete next[name];
      return next;
    });

    if (status.type !== "idle") {
      setStatus({
        type: "idle",
        message: "",
        orderNumber: "",
      });
    }
  }

  function focusFirstFieldError(errors) {
    const firstFieldName = FIELD_FOCUS_ORDER.find(
      (fieldName) => errors[fieldName]
    );

    if (!firstFieldName) {
      return;
    }

    window.requestAnimationFrame(() => {
      const target = formRef.current?.querySelector(
        `[name="${firstFieldName}"]`
      );

      if (!target) {
        return;
      }

      target.focus();
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }

  function validateForm() {
    const errors = {};
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const company = form.company.trim();
    const businessId = form.businessId.trim();
    const message = form.message.trim();

    if (name.length < 2) {
      errors.name = t("contactPage.validation.nameTooShort", {
        defaultValue: "Anna nimesi vähintään kahdella merkillä.",
      });
    } else if (name.length > 120) {
      errors.name = t("contactPage.validation.nameTooLong", {
        defaultValue: "Nimi saa olla enintään 120 merkkiä.",
      });
    }

    if (!EMAIL_PATTERN.test(email)) {
      errors.email = t("contactPage.validation.emailInvalid", {
        defaultValue: "Anna toimiva sähköpostiosoite.",
      });
    } else if (email.length > 180) {
      errors.email = t("contactPage.validation.emailTooLong", {
        defaultValue: "Sähköposti saa olla enintään 180 merkkiä.",
      });
    }

    if (!isValidPhone(phone)) {
      errors.phone = t("contactPage.validation.phoneInvalid", {
        defaultValue: "Anna puhelinnumero, jossa on 6–15 numeroa.",
      });
    } else if (phone.length > 30) {
      errors.phone = t("contactPage.validation.phoneTooLong", {
        defaultValue: "Puhelinnumero saa olla enintään 30 merkkiä.",
      });
    }

    if (isPrintOrderMode) {
      if (!orderDraft) {
        errors.product = t("contactPage.validation.orderDraftMissing", {
          defaultValue:
            "Tilausluonnosta ei löytynyt. Palaa tuote-editoriin ja jatka sieltä uudelleen.",
        });
      }

      if (company.length > 160) {
        errors.company = t("contactPage.validation.companyTooLong", {
          defaultValue: "Yrityksen nimi saa olla enintään 160 merkkiä.",
        });
      }

      if (businessId) {
        if (!company) {
          errors.company = t("contactPage.validation.companyRequiredWithBusinessId", {
            defaultValue: "Anna yrityksen nimi, kun Y-tunnus on täytetty.",
          });
        }

        if (!/^\d{7}-\d$/.test(businessId)) {
          errors.businessId = t("contactPage.validation.businessIdFormat", {
            defaultValue: "Tarkista Y-tunnus. Muodon pitää olla 1234567-8.",
          });
        } else if (!isValidFinnishBusinessId(businessId)) {
          errors.businessId = t("contactPage.validation.businessIdChecksum", {
            defaultValue: "Y-tunnuksen tarkistusnumero ei täsmää.",
          });
        }
      }

      if (isShipping) {
        const streetAddress = form.streetAddress.trim();
        const postalCode = form.postalCode.trim();
        const city = form.city.trim();

        if (streetAddress.length < 3) {
          errors.streetAddress = t("contactPage.validation.streetRequired", {
            defaultValue: "Anna toimituksen katuosoite.",
          });
        } else if (streetAddress.length > 180) {
          errors.streetAddress = t("contactPage.validation.streetTooLong", {
            defaultValue: "Katuosoite saa olla enintään 180 merkkiä.",
          });
        }

        if (!/^\d{5}$/.test(postalCode)) {
          errors.postalCode = t("contactPage.validation.postalCodeInvalid", {
            defaultValue: "Postinumerossa pitää olla viisi numeroa.",
          });
        }

        if (city.length < 2) {
          errors.city = t("contactPage.validation.cityRequired", {
            defaultValue: "Anna postitoimipaikka.",
          });
        } else if (city.length > 100) {
          errors.city = t("contactPage.validation.cityTooLong", {
            defaultValue: "Postitoimipaikka saa olla enintään 100 merkkiä.",
          });
        }
      }

      if (message.length > 2000) {
        errors.message = t("contactPage.validation.orderMessageTooLong", {
          defaultValue: "Lisätiedot saavat olla enintään 2000 merkkiä.",
        });
      }

      if (!form.acceptedProof) {
        errors.acceptedProof = t("contactPage.validation.proofRequired", {
          defaultValue: "Vahvista, että olet tarkistanut tilauksen tiedot.",
        });
      }

      return errors;
    }

    const topicIsValid = CONTACT_TOPICS.some(
      (topic) => topic.value === form.topic
    );

    if (!topicIsValid) {
      errors.topic = t("contactPage.validation.topicRequired", {
        defaultValue: "Valitse viestin aihe.",
      });
    }

    if (message.length < 5) {
      errors.message = t("contactPage.validation.messageTooShort", {
        defaultValue: "Kirjoita vähintään viiden merkin viesti.",
      });
    } else if (message.length > 3000) {
      errors.message = t("contactPage.validation.messageTooLong", {
        defaultValue: "Viesti saa olla enintään 3000 merkkiä.",
      });
    }

    return errors;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (status.type === "loading") {
      return;
    }

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setStatus({
        type: "error",
        message: t("contactPage.status.checkFields", {
          defaultValue: "Tarkista lomakkeeseen merkityt tiedot.",
        }),
        orderNumber: "",
      });
      focusFirstFieldError(validationErrors);
      return;
    }

    setFieldErrors({});
    setStatus({
      type: "loading",
      message: "",
      orderNumber: "",
    });

    const apiBaseUrl = getApiBaseUrl();

    const customer = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      company: form.company.trim(),
      businessId: form.businessId.trim(),
    };

    const delivery = isPrintOrderMode
      ? {
          method: orderDraft?.deliveryMethod || "pickup",
          label: orderDraft?.deliveryLabel || "Nouto",

          // Tämä lähetetään mukana käyttöliittymän tiedoksi,
          // mutta backend tarkistaa hinnan itse.
          price: Number(orderDraft?.deliveryPrice) || 0,

          streetAddress: isShipping ? form.streetAddress.trim() : "",
          postalCode: isShipping ? form.postalCode.trim() : "",
          city: isShipping ? form.city.trim() : "",
        }
      : null;

    const endpoint = isPrintOrderMode
      ? `${apiBaseUrl}/print-orders`
      : `${apiBaseUrl}/contact`;

    const payload = isPrintOrderMode
      ? {
          type: "print_order",
          language: emailLanguage,
          customer,
          delivery,
          additionalNotes: form.message.trim(),
          acceptedProof: form.acceptedProof,
          order: {
            ...orderDraft,
            logoDataUrl: orderDraft?.logoDataUrl || "",
            standPreviewDataUrl: orderDraft?.standPreviewDataUrl || "",
            coasterFrontPreviewDataUrl:
              orderDraft?.coasterFrontPreviewDataUrl || "",
            coasterBackPreviewDataUrl:
              orderDraft?.coasterBackPreviewDataUrl || "",
          },
          sourceUrl: window.location.href,
        }
      : {
          type: "contact",
          language: emailLanguage,
          customer,
          topic: form.topic,
          message: form.message.trim(),
          sourceUrl: window.location.href,
        };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.ok) {
        const fallbackMessage = isPrintOrderMode
          ? t("contactPage.status.orderSendFailed", {
              defaultValue: "Tilauksen lähettäminen epäonnistui.",
            })
          : t("contactPage.status.messageSendFailed", {
              defaultValue: "Viestin lähettäminen epäonnistui.",
            });

        const requestError = new Error(
          isEnglish ? fallbackMessage : data?.error || fallbackMessage
        );

        requestError.fieldErrors = localizeServerFieldErrors(
          data?.fieldErrors,
          t,
          isEnglish
        );

        throw requestError;
      }

      if (isPrintOrderMode) {
        window.sessionStorage.removeItem(PRINT_ORDER_DRAFT_KEY);
      }

      const fallbackSuccessMessage = isPrintOrderMode
        ? t("contactPage.status.orderReceived", {
            defaultValue: "Tilaus on vastaanotettu.",
          })
        : t("contactPage.status.messageSent", {
            defaultValue: "Viesti lähetettiin. Palaan asiaan mahdollisimman pian.",
          });

      setStatus({
        type: "success",
        message: isEnglish
          ? fallbackSuccessMessage
          : data?.message || fallbackSuccessMessage,
        orderNumber: data?.orderNumber || data?.messageNumber || "",
      });
    } catch (error) {
      const responseFieldErrors = localizeServerFieldErrors(
        error?.fieldErrors,
        t,
        isEnglish
      );

      setFieldErrors(responseFieldErrors);

      const fallbackMessage = isPrintOrderMode
        ? t("contactPage.status.orderSendFailed", {
            defaultValue: "Tilauksen lähettäminen epäonnistui.",
          })
        : t("contactPage.status.messageSendFailed", {
            defaultValue: "Viestin lähettäminen epäonnistui.",
          });

      setStatus({
        type: "error",
        message: error?.message || fallbackMessage,
        orderNumber: "",
      });

      if (Object.keys(responseFieldErrors).length > 0) {
        focusFirstFieldError(responseFieldErrors);
      }
    }
  }

  if (isPrintOrderMode && !orderDraft) {
    return (
      <main className="contact-page min-h-screen bg-black px-4 py-16 text-white sm:px-6">
        <section className="contact-state-card mx-auto max-w-3xl rounded-[2rem] border border-red-400/25 bg-zinc-950/95 p-7 text-center shadow-2xl shadow-black/50 sm:p-10">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-red-300">
            {t("contactPage.missingDraft.eyebrow", {
              defaultValue: "Painotuotetilaus",
            })}
          </p>

          <h1 className="mt-4 text-4xl font-black tracking-[-0.04em] text-white">
            {t("contactPage.missingDraft.title", {
              defaultValue: "Tilausluonnosta ei löytynyt",
            })}
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base leading-8 text-zinc-400">
            {t("contactPage.missingDraft.description", {
              defaultValue:
                "Palaa tuote-editoriin, tarkista tuote ja valitse Jatka tarjouspyyntöön uudelleen.",
            })}
          </p>

          <a
            href="/taydenna-tilaustasi#editori"
            className="mt-8 inline-flex rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-white no-underline transition hover:bg-red-400"
          >
            {t("contactPage.missingDraft.openEditor", {
              defaultValue: "Avaa tuote-editori",
            })}
          </a>
        </section>
      </main>
    );
  }

  if (status.type === "success") {
    return (
      <main className="contact-page min-h-screen bg-black px-4 py-16 text-white sm:px-6">
        <section className="contact-state-card contact-success-card mx-auto max-w-3xl rounded-[2rem] border border-emerald-400/25 bg-gradient-to-br from-emerald-500/10 via-zinc-950 to-black p-7 text-center shadow-2xl shadow-black/50 sm:p-10">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-3xl font-black text-white">
            ✓
          </div>

          <p className="mt-6 text-xs font-black uppercase tracking-[0.2em] text-emerald-300">
            {isPrintOrderMode
              ? t("contactPage.success.orderEyebrow", {
                  defaultValue: "Tilaus vastaanotettu",
                })
              : t("contactPage.success.messageEyebrow", {
                  defaultValue: "Viesti lähetetty",
                })}
          </p>

          <h1 className="mt-4 text-4xl font-black tracking-[-0.04em] text-white sm:text-5xl">
            {t("contactPage.success.thanks", { defaultValue: "Kiitos!" })}
          </h1>

          {status.orderNumber && (
            <p className="mt-5 text-sm font-black uppercase tracking-[0.12em] text-zinc-300">
              {isPrintOrderMode
                ? t("contactPage.success.orderNumber", {
                    defaultValue: "Tilausnumero",
                  })
                : t("contactPage.success.messageNumber", {
                    defaultValue: "Viestinumero",
                  })}{" "}
              {status.orderNumber}
            </p>
          )}

          <p className="mx-auto mt-5 max-w-xl text-base leading-8 text-zinc-400">
            {status.message}
          </p>

          <a
            href={isPrintOrderMode ? "/admin" : "/"}
            className="mt-8 inline-flex rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-white no-underline transition hover:bg-red-400"
          >
            {isPrintOrderMode
              ? t("contactPage.success.backToAdmin", {
                  defaultValue: "Palaa hallintaan",
                })
              : t("contactPage.success.backHome", {
                  defaultValue: "Palaa etusivulle",
                })}
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="contact-page min-h-screen overflow-hidden bg-black px-4 pb-12 pt-28 text-white sm:px-6 sm:pt-32">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(239,68,68,0.18),transparent_36rem)]" />
      <div className="contact-page-grid pointer-events-none fixed inset-0 opacity-[0.045] [background-image:linear-gradient(rgba(255,255,255,.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.8)_1px,transparent_1px)] [background-size:42px_42px]" />

      <div className="relative mx-auto max-w-7xl">
        <header className="contact-page-header mb-10 max-w-4xl">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-red-400">
            Paranoidi
          </p>

          <h1 className="mt-4 text-4xl font-black tracking-[-0.05em] text-white sm:text-6xl">
            {isPrintOrderMode
              ? t("contactPage.hero.printTitle", {
                  defaultValue: "Viimeistele painotuotetilaus",
                })
              : t("contactPage.hero.contactTitle", {
                  defaultValue: "Ota yhteyttä",
                })}
          </h1>

          <p className="mt-5 max-w-3xl text-base font-semibold leading-8 text-zinc-400 sm:text-lg">
            {isPrintOrderMode
              ? t("contactPage.hero.printIntro", {
                  defaultValue:
                    "Tarkista vedos ja tilauksen yhteenveto, täytä yhteystietosi ja hyväksy tilaus.",
                })
              : t("contactPage.hero.contactIntro", {
                  defaultValue:
                    "Kysy Paranoidin käyttöönotosta, ominaisuuksista, tilauksesta tai teknisestä tuesta. Vastaan mahdollisimman pian.",
                })}
          </p>
        </header>

        <div
          className={
            isPrintOrderMode
              ? "grid items-start gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(24rem,0.95fr)]"
              : "grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.72fr)]"
          }
        >
          <div>
            {isPrintOrderMode ? (
              <OrderSummary draft={orderDraft} t={t} locale={locale} />
            ) : (
              <section className="contact-help-panel rounded-[2rem] border border-white/10 bg-zinc-950/90 p-6 shadow-2xl shadow-black/40 sm:p-8">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300">
                  {t("contactPage.help.eyebrow", {
                    defaultValue: "Miten voin auttaa?",
                  })}
                </p>

                <div className="mt-5 grid gap-4">
                  <div className="contact-help-card rounded-3xl border border-white/10 bg-black/25 p-5">
                    <h2 className="text-xl font-black text-white">
                      {t("contactPage.help.sales.title", {
                        defaultValue: "Myynti ja käyttöönotto",
                      })}
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-zinc-400">
                      {t("contactPage.help.sales.text", {
                        defaultValue:
                          "Kerro esiintyjästäsi ja siitä, miten haluat käyttää Paranoidia keikoilla tai tapahtumissa.",
                      })}
                    </p>
                  </div>

                  <div className="contact-help-card rounded-3xl border border-white/10 bg-black/25 p-5">
                    <h2 className="text-xl font-black text-white">
                      {t("contactPage.help.support.title", {
                        defaultValue: "Tekninen tuki",
                      })}
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-zinc-400">
                      {t("contactPage.help.support.text", {
                        defaultValue:
                          "Kerro esiintyjän nimi, mitä olit tekemässä ja mitä tapahtui. Älä lähetä salasanaa viestissä.",
                      })}
                    </p>
                  </div>

                  <div className="contact-help-card rounded-3xl border border-white/10 bg-black/25 p-5">
                    <h2 className="text-xl font-black text-white">
                      {t("contactPage.help.billing.title", {
                        defaultValue: "Tilaus ja laskutus",
                      })}
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-zinc-400">
                      {t("contactPage.help.billing.text", {
                        defaultValue:
                          "Pakettien hallinta tapahtuu Tilaus-sivulla. Epäselvissä tilanteissa voit lähettää tiedot tällä lomakkeella.",
                      })}
                    </p>
                  </div>
                </div>
              </section>
            )}
          </div>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            aria-busy={status.type === "loading"}
            className="contact-form rounded-[2rem] border border-white/10 bg-zinc-950/95 p-5 shadow-2xl shadow-black/50 sm:p-7"
          >
            <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300">
              {isPrintOrderMode
                ? t("contactPage.form.orderEyebrow", {
                    defaultValue: "Tilaajan tiedot",
                  })
                : t("contactPage.form.contactEyebrow", {
                    defaultValue: "Lähetä viesti",
                  })}
            </p>

            <div className="mt-6 grid gap-5">
              <label>
                <FieldLabel required>
                  {t("contactPage.form.name", { defaultValue: "Nimi" })}
                </FieldLabel>
                <TextInput
                  id="contact-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={updateField}
                  autoComplete="name"
                  maxLength={120}
                  hasError={Boolean(fieldErrors.name)}
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={
                    fieldErrors.name ? "contact-name-error" : undefined
                  }
                />
                <FieldError id="contact-name-error">
                  {fieldErrors.name}
                </FieldError>
              </label>

              <label>
                <FieldLabel required>
                  {t("contactPage.form.email", { defaultValue: "Sähköposti" })}
                </FieldLabel>
                <TextInput
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={updateField}
                  autoComplete="email"
                  maxLength={180}
                  inputMode="email"
                  hasError={Boolean(fieldErrors.email)}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={
                    fieldErrors.email ? "contact-email-error" : undefined
                  }
                />
                <FieldError id="contact-email-error">
                  {fieldErrors.email}
                </FieldError>
              </label>

              <label>
                <FieldLabel required>
                  {t("contactPage.form.phone", {
                    defaultValue: "Puhelinnumero",
                  })}
                </FieldLabel>
                <TextInput
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={updateField}
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength={30}
                  placeholder="+358 40 123 4567"
                  hasError={Boolean(fieldErrors.phone)}
                  aria-invalid={Boolean(fieldErrors.phone)}
                  aria-describedby={
                    fieldErrors.phone ? "contact-phone-error" : undefined
                  }
                />
                <FieldError id="contact-phone-error">
                  {fieldErrors.phone}
                </FieldError>
              </label>

              {isPrintOrderMode ? (
                <>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label>
                      <FieldLabel>
                        {t("contactPage.form.company", {
                          defaultValue: "Yritys",
                        })}
                      </FieldLabel>
                      <TextInput
                        id="contact-company"
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={updateField}
                        autoComplete="organization"
                        maxLength={160}
                        hasError={Boolean(fieldErrors.company)}
                        aria-invalid={Boolean(fieldErrors.company)}
                        aria-describedby={
                          fieldErrors.company
                            ? "contact-company-error"
                            : undefined
                        }
                      />
                      <FieldError id="contact-company-error">
                        {fieldErrors.company}
                      </FieldError>
                    </label>

                    <label>
                      <FieldLabel>
                        {t("contactPage.form.businessId", {
                          defaultValue: "Y-tunnus",
                        })}
                      </FieldLabel>
                      <TextInput
                        id="contact-business-id"
                        type="text"
                        name="businessId"
                        value={form.businessId}
                        onChange={updateField}
                        autoComplete="off"
                        maxLength={9}
                        placeholder="1234567-8"
                        hasError={Boolean(fieldErrors.businessId)}
                        aria-invalid={Boolean(fieldErrors.businessId)}
                        aria-describedby={
                          fieldErrors.businessId
                            ? "contact-business-id-error"
                            : undefined
                        }
                      />
                      <FieldError id="contact-business-id-error">
                        {fieldErrors.businessId}
                      </FieldError>
                    </label>
                  </div>

                  {isShipping && (
                    <div className="contact-address-panel rounded-3xl border border-white/10 bg-black/25 p-4">
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-300">
                        {t("contactPage.form.deliveryAddress", {
                          defaultValue: "Toimitusosoite",
                        })}
                      </p>

                      <div className="mt-4 grid gap-4">
                        <label>
                          <FieldLabel required>
                            {t("contactPage.form.streetAddress", {
                              defaultValue: "Katuosoite",
                            })}
                          </FieldLabel>
                          <TextInput
                            id="contact-street-address"
                            type="text"
                            name="streetAddress"
                            value={form.streetAddress}
                            onChange={updateField}
                            autoComplete="street-address"
                            maxLength={180}
                            hasError={Boolean(fieldErrors.streetAddress)}
                            aria-invalid={Boolean(fieldErrors.streetAddress)}
                            aria-describedby={
                              fieldErrors.streetAddress
                                ? "contact-street-address-error"
                                : undefined
                            }
                          />
                          <FieldError id="contact-street-address-error">
                            {fieldErrors.streetAddress}
                          </FieldError>
                        </label>

                        <div className="grid gap-4 sm:grid-cols-[0.7fr_1.3fr]">
                          <label>
                            <FieldLabel required>
                              {t("contactPage.form.postalCode", {
                                defaultValue: "Postinumero",
                              })}
                            </FieldLabel>
                            <TextInput
                              id="contact-postal-code"
                              type="text"
                              name="postalCode"
                              value={form.postalCode}
                              onChange={updateField}
                              autoComplete="postal-code"
                              inputMode="numeric"
                              maxLength={5}
                              placeholder="00100"
                              hasError={Boolean(fieldErrors.postalCode)}
                              aria-invalid={Boolean(fieldErrors.postalCode)}
                              aria-describedby={
                                fieldErrors.postalCode
                                  ? "contact-postal-code-error"
                                  : undefined
                              }
                            />
                            <FieldError id="contact-postal-code-error">
                              {fieldErrors.postalCode}
                            </FieldError>
                          </label>

                          <label>
                            <FieldLabel required>
                              {t("contactPage.form.city", {
                                defaultValue: "Postitoimipaikka",
                              })}
                            </FieldLabel>
                            <TextInput
                              id="contact-city"
                              type="text"
                              name="city"
                              value={form.city}
                              onChange={updateField}
                              autoComplete="address-level2"
                              maxLength={100}
                              hasError={Boolean(fieldErrors.city)}
                              aria-invalid={Boolean(fieldErrors.city)}
                              aria-describedby={
                                fieldErrors.city
                                  ? "contact-city-error"
                                  : undefined
                              }
                            />
                            <FieldError id="contact-city-error">
                              {fieldErrors.city}
                            </FieldError>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <label>
                    <FieldLabel>
                      {t("contactPage.form.orderNotes", {
                        defaultValue: "Lisätiedot tilaukseen",
                      })}
                    </FieldLabel>
                    <TextArea
                      id="contact-message"
                      name="message"
                      value={form.message}
                      onChange={updateField}
                      maxLength={2000}
                      placeholder={t("contactPage.form.orderNotesPlaceholder", {
                        defaultValue:
                          "Esimerkiksi toimitukseen, laskutukseen tai aineistoon liittyvä lisätieto.",
                      })}
                      hasError={Boolean(fieldErrors.message)}
                      aria-invalid={Boolean(fieldErrors.message)}
                      aria-describedby={
                        fieldErrors.message
                          ? "contact-message-error"
                          : "contact-message-count"
                      }
                    />
                    <div className="mt-2 flex items-start justify-between gap-4">
                      <FieldError id="contact-message-error">
                        {fieldErrors.message}
                      </FieldError>

                      <span
                        id="contact-message-count"
                        className="ml-auto shrink-0 text-xs font-bold text-zinc-500"
                      >
                        {form.message.length}/2000
                      </span>
                    </div>
                  </label>

                  <label
                    className={[
                      "contact-approval",
                      "cursor-pointer rounded-2xl border p-4",
                      fieldErrors.acceptedProof
                        ? "border-red-400/70 bg-red-500/[0.12]"
                        : "border-red-400/20 bg-red-500/[0.07]",
                    ].join(" ")}
                  >
                    <span className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        name="acceptedProof"
                        checked={form.acceptedProof}
                        onChange={updateField}
                        className="mt-1 h-5 w-5 shrink-0 accent-red-500"
                        aria-invalid={Boolean(fieldErrors.acceptedProof)}
                        aria-describedby={
                          fieldErrors.acceptedProof
                            ? "contact-accepted-proof-error"
                            : undefined
                        }
                      />

                      <span className="text-sm font-semibold leading-7 text-zinc-300">
                        {t("contactPage.form.acceptProof", {
                          defaultValue:
                            "Olen tarkistanut vedoksen ja tilauksen tiedot ja hyväksyn ne painotuotteen valmistusta varten.",
                        })}
                      </span>
                    </span>

                    <FieldError id="contact-accepted-proof-error">
                      {fieldErrors.acceptedProof}
                    </FieldError>
                  </label>
                </>
              ) : (
                <>
                  <label>
                    <FieldLabel required>
                      {t("contactPage.form.topic", { defaultValue: "Aihe" })}
                    </FieldLabel>
                    <select
                      id="contact-topic"
                      name="topic"
                      value={form.topic}
                      onChange={updateField}
                      aria-invalid={Boolean(fieldErrors.topic)}
                      aria-describedby={
                        fieldErrors.topic ? "contact-topic-error" : undefined
                      }
                      className={[
                        "contact-field",
                        "w-full rounded-2xl border bg-black/45 px-4 py-3.5",
                        "text-sm font-semibold text-white outline-none transition",
                        "focus:border-red-400/60 focus:ring-4 focus:ring-red-500/10",
                        fieldErrors.topic
                          ? "border-red-400/70 bg-red-500/[0.06]"
                          : "border-white/10",
                      ].join(" ")}
                    >
                      {CONTACT_TOPICS.map((topic) => (
                        <option key={topic.value} value={topic.value}>
                          {t(`contactPage.topics.${topic.key}`)}
                        </option>
                      ))}
                    </select>
                    <FieldError id="contact-topic-error">
                      {fieldErrors.topic}
                    </FieldError>
                  </label>

                  <label>
                    <FieldLabel required>
                      {t("contactPage.form.message", { defaultValue: "Viesti" })}
                    </FieldLabel>
                    <TextArea
                      id="contact-message"
                      name="message"
                      value={form.message}
                      onChange={updateField}
                      maxLength={3000}
                      placeholder={t("contactPage.form.messagePlaceholder", {
                        defaultValue: "Kerro lyhyesti, miten voin auttaa.",
                      })}
                      hasError={Boolean(fieldErrors.message)}
                      aria-invalid={Boolean(fieldErrors.message)}
                      aria-describedby={
                        fieldErrors.message
                          ? "contact-message-error"
                          : "contact-message-count"
                      }
                    />
                    <div className="mt-2 flex items-start justify-between gap-4">
                      <FieldError id="contact-message-error">
                        {fieldErrors.message}
                      </FieldError>

                      <span
                        id="contact-message-count"
                        className="ml-auto shrink-0 text-xs font-bold text-zinc-500"
                      >
                        {form.message.length}/3000
                      </span>
                    </div>
                  </label>
                </>
              )}

              {status.type === "error" && (
                <p className="rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm font-bold leading-6 text-red-200">
                  {status.message}
                </p>
              )}

              <button
                type="submit"
                disabled={status.type === "loading"}
                className="inline-flex w-full items-center justify-center rounded-full bg-red-500 px-6 py-4 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:bg-red-400 disabled:cursor-wait disabled:opacity-60"
              >
                {status.type === "loading"
                  ? t("contactPage.form.sending", { defaultValue: "Lähetetään…" })
                  : isPrintOrderMode
                    ? t("contactPage.form.sendOrder", {
                        defaultValue: "Lähetä tilaus",
                      })
                    : t("contactPage.form.sendMessage", {
                        defaultValue: "Lähetä viesti",
                      })}
              </button>

              <p className="text-center text-xs font-semibold leading-5 text-zinc-500">
                {t("contactPage.form.privacyNote", {
                  defaultValue:
                    "Tietoja käytetään vain viestin tai tilauksen käsittelyyn.",
                })}
              </p>
            </div>
          </form>
        </div>

        <section className="contact-quick-panel mt-8 flex flex-col gap-4 rounded-[2rem] border border-white/10 bg-zinc-950/90 p-5 shadow-2xl shadow-black/40 sm:flex-row sm:items-center sm:justify-between sm:p-7">
          <div>
            <h2 className="text-xl font-black text-white">
              {t("contactPage.quick.title", {
                defaultValue: "Haluatko kysyä nopeasti?",
              })}
            </h2>

            <p className="mt-2 text-sm leading-7 text-zinc-400">
              {t("contactPage.quick.text", {
                defaultValue:
                  "Voit lähettää sähköpostia osoitteeseen info@buukkeri.fi tai avata WhatsApp-keskustelun.",
              })}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href="mailto:info@buukkeri.fi?subject=Paranoidi"
              className="inline-flex justify-center rounded-full border border-white/15 px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-white no-underline transition hover:border-white/30 hover:bg-white/10"
            >
              {t("contactPage.quick.email", { defaultValue: "Sähköposti" })}
            </a>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center rounded-full border border-emerald-400/40 bg-emerald-500/15 px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-emerald-100 no-underline transition hover:bg-emerald-500/25"
            >
              WhatsApp
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
